/**
 * 品牌配置加载工具
 * 利用构建时常量实现 tree-shaking，确保只打包当前品牌的代码
 *
 * @example
 * ```ts
 * import { loadBrandConfig } from '@/utils/loadBrandConfig'
 *
 * const config = loadBrandConfig({
 *   rocnovel: rocnovelConfig,
 *   oceread: ocereadConfig,
 *   ficmoon: ficmoonConfig,
 * })
 * ```
 */

type BrandName = 'rocnovel' | 'oceread' | 'talewave' | 'ficmoon' | 'novelarc' | 'storyella' | 'silktale' | 'pearla' | 'fictora' | 'marinovel' | 'flipnovel' | 'moonpage' | 'storlyn' | 'novaris'
type BrandValueMap<T> = Partial<Record<BrandName, T>>

// 声明构建时注入的品牌常量
declare const __BRAND__: BrandName

function pickFirstDefined<T>(configs: BrandValueMap<T>): T {
  const fallback = Object.values(configs).find((item): item is T => item !== undefined)
  if (fallback === undefined)
    throw new Error(`No brand config found for ${__BRAND__}`)

  return fallback
}

function resolveBrandValue<T>(configs: BrandValueMap<T>): T {
  if (__BRAND__ === 'rocnovel')
    return configs.rocnovel ?? configs.novelarc ?? configs.oceread ?? pickFirstDefined(configs)
  if (__BRAND__ === 'oceread')
    return configs.oceread ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'ficmoon')
    return configs.ficmoon ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'storyella')
    return configs.storyella ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'silktale')
    return configs.silktale ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'pearla')
    return configs.pearla ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'fictora')
    return configs.fictora ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'marinovel')
    return configs.marinovel ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'talewave')
    return configs.talewave ?? configs.oceread ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'moonpage')
    return configs.moonpage ?? configs.flipnovel ?? configs.storlyn ?? pickFirstDefined(configs)
  if (__BRAND__ === 'storlyn')
    return configs.storlyn ?? configs.flipnovel ?? configs.moonpage ?? pickFirstDefined(configs)
  if (__BRAND__ === 'novaris')
    return configs.novaris ?? configs.storlyn ?? configs.flipnovel ?? configs.moonpage ?? pickFirstDefined(configs)
  if (__BRAND__ === 'flipnovel')
    return configs.flipnovel ?? configs.storlyn ?? configs.moonpage ?? pickFirstDefined(configs)
  return configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
}

/**
 * 加载品牌配置
 *
 * 构建时会根据 __BRAND__ 常量进行 tree-shaking，
 * 只保留当前品牌的配置，移除其他品牌的配置
 */
export function loadBrandConfig<T>(configs: BrandValueMap<T>): T {
  return resolveBrandValue(configs)
}

/**
 * 加载品牌专属组件
 *
 * 构建时会根据 __BRAND__ 常量进行 tree-shaking，
 * 只保留当前品牌的组件，移除其他品牌的组件
 */
export function loadBrandComponent<T>(components: BrandValueMap<T>): T {
  return resolveBrandValue(components)
}
